import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useApiQuery} from 'src/hooks/useApiQuery';
import {useHistory} from 'src/rerouter';
import {ApiError, captureApiError} from 'src/utils/errors';
import logger from 'src/utils/logger';
import {Editor, type SuggestionItem} from 'src/components/agentic-ui/editor';
import {type Part} from 'src/components/agentic-ui';
import {startNewConversation} from 'src/components/agentic-ui/ask-ai-store';
import {toSuggestionSections} from 'src/utils/askai-typeahead';
import type {TypeaheadApiItem} from 'src/types/askai-typeahead';

import css from './askai-typeahead.css';


const SOURCE = 'askai_typeahead';

// Grace service requires a custom apiPath prefix instead of the default /api/v1/.
const GRACE_API_OPTIONS = {options: {apiPath: '/grace/api/v1/'}} as const;

function AskAiTypeahead(): React.ReactElement {
  const {t} = useLabelTranslation();
  const history = useHistory();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce keystrokes before issuing a typeahead request.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(id);
  }, [query]);

  const trimmedQuery = debouncedQuery.trim();

  const {data, isFetching, error} = useApiQuery<TypeaheadApiItem[]>(
    {pathname: 'typeahead', query: {q: trimmedQuery}},
    {
      queryOptions: {enabled: trimmedQuery.length > 0},
      apiOptions: GRACE_API_OPTIONS,
    },
  );

  useEffect(() => {
    if (error instanceof ApiError) {
      captureApiError(error);
    }
  }, [error]);

  const suggestions = useMemo(
    () => toSuggestionSections(data ?? [], t),
    [data, t],
  );

  const startConversation = useCallback((message: string) => {
    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }
    const started = startNewConversation({source: SOURCE, message: trimmed});
    if (!started) {
      logger.info(
        'askai typeahead: chat panel not mounted, cannot start conversation',
      );
    }
  }, []);

  const handleSelect = useCallback(
    (item: SuggestionItem) => {
      if (item.meta === 'pages') {
        void history.push(item.key);
        return;
      }
      startConversation(item.label);
    },
    [history, startConversation],
  );

  const handleSend = useCallback(
    (parts: Part[]) => {
      const textPart = parts.find((p) => p.type === 'plain_text');
      const text = textPart != null && textPart.type === 'plain_text' ? textPart.data : '';
      startConversation(text);
    },
    [startConversation],
  );

  return (
    <div className={css.container}>
      <TitleMedium>
        {t('What_would_you_like_to_explore', 'What would you like to explore?')}
      </TitleMedium>
      <div className={css.inputWrap}>
        <Editor
          enableSuggestions
          enableMentions={false}
          enableUpload={false}
          enableVoice={false}
          suggestions={suggestions}
          suggestionsLoading={isFetching}
          onQueryChange={setQuery}
          onSelectSuggestion={handleSelect}
          onSend={handleSend}
          placeholder={t(
            'Search_for_any_page_report_or_type_your_query',
            'Search for any page, report or type your query',
          )}
        />
      </div>
    </div>
  );
}

export default AskAiTypeahead;
